import React, { useEffect, useState } from "react";
import { Building2, Bot, Check, AlertCircle, RefreshCw, UserPlus, Phone, Mail, MapPin, Trash2, Clock, LogIn, Users, ChevronDown, ChevronUp, Zap, FileText, Download, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Superadmin() {
    const [tenants, setTenants] = useState([]);
    const [leads, setLeads] = useState([]);
    const [tgStatus, setTgStatus] = useState(null);
    const [token, setToken] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [expandedTenant, setExpandedTenant] = useState(null);  // tenant id whose users are visible
    const [expandedUsers, setExpandedUsers] = useState([]);  // users of the expanded tenant
    const [showCreateTenant, setShowCreateTenant] = useState(false);
    const [createForm, setCreateForm] = useState({
        tenant_name: "", admin_first_name: "", admin_last_name: "", admin_email: "",
        phone_mobile: "", vat: "", city: "", plan: "pro", billing_cycle: "annual", send_email: true,
    });
    const [creating, setCreating] = useState(false);

    const submitCreateTenant = async () => {
        setError(""); setSuccess(""); setCreating(true);
        try {
            const { data } = await api.post("/superadmin/tenants", createForm);
            setSuccess(`Tenant "${createForm.tenant_name}" creato. Email: ${data.credentials.email} · Password: ${data.credentials.password}${data.email_sent ? " · Email inviata ✉️" : ""}`);
            setShowCreateTenant(false);
            setCreateForm({ tenant_name: "", admin_first_name: "", admin_last_name: "", admin_email: "", phone_mobile: "", vat: "", city: "", plan: "pro", billing_cycle: "annual", send_email: true });
            loadAll();
        } catch (e) { setError(formatApiError(e)); }
        setCreating(false);
    };

    const toggleTenantUsers = async (tenantId) => {
        if (expandedTenant === tenantId) {
            setExpandedTenant(null);
            setExpandedUsers([]);
            return;
        }
        setExpandedTenant(tenantId);
        setExpandedUsers([]);
        try {
            const { data } = await api.get(`/superadmin/tenants/${tenantId}/users`);
            setExpandedUsers(data);
        } catch (e) { setError(formatApiError(e)); }
    };

    const impersonate = async (userId, email) => {
        if (!window.confirm(`Accedere come ${email}? Verrai loggato come questo utente. L'accesso è registrato nello storico interno.`)) return;
        try {
            await api.post(`/superadmin/impersonate/${userId}`);
            window.location.href = "/dashboard";
        } catch (e) { setError(formatApiError(e)); }
    };

    const [activating, setActivating] = useState(null);  // lead being activated
    const [activateForm, setActivateForm] = useState({ plan: "pro", billing_cycle: "annual", setup_fee_paid: false, send_email: true });
    const [activateResult, setActivateResult] = useState(null);

    const submitActivate = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post(`/superadmin/leads/${activating.id}/activate`, activateForm);
            setActivateResult(data);
            loadAll();
        } catch (e) { setError(formatApiError(e)); }
    };

    const downloadContract = (id, plan = "pro", cycle = "annual") => {
        window.open(`${""}/api/superadmin/tenants/${id}/contract.pdf?plan=${plan}&billing_cycle=${cycle}`, "_blank");
    };

    const exportTenantZip = (id) => {
        window.open(`${""}/api/superadmin/tenants/${id}/export.zip`, "_blank");
    };

    const loadAll = async () => {
        try {
            const [t, l, s] = await Promise.all([
                api.get("/superadmin/tenants"),
                api.get("/superadmin/leads"),
                api.get("/superadmin/telegram/status"),
            ]);
            setTenants(t.data);
            setLeads(l.data);
            setTgStatus(s.data);
        } catch { /* silent */ }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [t, l, s] = await Promise.all([
                    api.get("/superadmin/tenants"),
                    api.get("/superadmin/leads"),
                    api.get("/superadmin/telegram/status"),
                ]);
                if (!cancelled) {
                    setTenants(t.data);
                    setLeads(l.data);
                    setTgStatus(s.data);
                }
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const saveBotToken = async (e) => {
        e.preventDefault();
        setError(""); setSuccess(""); setSaving(true);
        try {
            const { data } = await api.post("/superadmin/telegram/configure", { token });
            if (data.ok) {
                setSuccess(`Bot @${data.bot?.username} attivato! Webhook: ${data.webhook_url}`);
                setToken("");
                loadAll();
            } else {
                setError(`Errore: ${data.webhook_result?.description || "configurazione fallita"}`);
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="superadmin-page">
            <div>
                <h1
                    className="tracking-tight"
                    style={{
                        color: "var(--navy)",
                        fontFamily: "Geist, Outfit, sans-serif",
                        fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                        fontWeight: 200,
                        letterSpacing: "-0.035em",
                        lineHeight: 1.05,
                    }}
                >
                    Super<span style={{ fontWeight: 600 }}>admin</span> TotalPL
                </h1>
                <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>Configurazione piattaforma + gestione tenant</p>
            </div>

            {/* Quick launch: FORGE Cognitive OS */}
            <ForgeQuickLaunch />

            {/* Platform Telegram Bot */}
            <div className="card-tpl p-6">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "var(--mod-accent-soft)" }}>
                        <Bot size={18} style={{ color: "var(--mod-accent)" }} />
                    </div>
                    <div>
                        <h3 className="text-[16px] font-extrabold" style={{ color: "var(--navy)" }}>Bot Telegram Piattaforma</h3>
                        <p className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>
                            Un solo bot serve tutti i tenant. Configura il token ricevuto da <code className="mono">@BotFather</code>.
                        </p>
                    </div>
                </div>

                {tgStatus?.configured ? (
                    <div className="p-4 rounded-lg mb-4" style={{ background: "var(--green-soft)", border: "1px solid var(--green)" }}>
                        <div className="flex items-center gap-3">
                            <Check size={18} style={{ color: "var(--green)" }} />
                            <div className="flex-1">
                                <div className="font-bold text-[14px]" style={{ color: "var(--green)" }}>
                                    Bot attivo: @{tgStatus.bot?.username}
                                </div>
                                <div className="text-[11.5px] mono mt-1" style={{ color: "var(--text-2)" }}>
                                    Webhook: {tgStatus.webhook_url}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 rounded-lg mb-4" style={{ background: "rgba(245,158,11,.08)", border: "1px solid var(--amber)" }}>
                        <div className="flex items-center gap-3">
                            <AlertCircle size={18} style={{ color: "var(--amber)" }} />
                            <div className="font-bold text-[14px]" style={{ color: "var(--amber)" }}>
                                Nessun bot configurato. Incolla il token per attivarlo.
                            </div>
                        </div>
                    </div>
                )}

                <form onSubmit={saveBotToken} className="space-y-3">
                    <div>
                        <label className="block mono text-[10px] uppercase tracking-[0.08em] font-bold mb-1.5" style={{ color: "var(--muted)" }}>
                            Token Bot (da @BotFather)
                        </label>
                        <div className="flex gap-2">
                            <input
                                data-testid="sa-bot-token"
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="7849273:AAEh-cD7vHm-OQwerty..."
                                className="input flex-1 mono"
                            />
                            <button data-testid="sa-bot-save" type="submit" disabled={saving || !token} className="btn-grad px-5 rounded-lg font-bold text-[13px]">
                                {saving ? "Configurazione..." : tgStatus?.configured ? "Aggiorna" : "Attiva bot"}
                            </button>
                        </div>
                        <p className="text-[11.5px] mt-2" style={{ color: "var(--muted)" }}>
                            🔒 Il token viene salvato cifrato. Verrà anche registrato il webhook su Telegram.
                        </p>
                    </div>
                    {error && <div className="p-3 rounded-lg text-[12.5px]" style={{ background: "rgba(239,68,68,.1)", color: "var(--red)" }}>{error}</div>}
                    {success && <div className="p-3 rounded-lg text-[12.5px]" style={{ background: "var(--green-soft)", color: "var(--green)" }}>{success}</div>}
                </form>
            </div>

            {/* Lead in attesa */}
            <div data-testid="leads-section">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <UserPlus size={18} style={{ color: "var(--mod-accent)" }} />
                        <h2 className="text-[18px] font-extrabold" style={{ color: "var(--navy)" }}>Lead in attesa</h2>
                        {leads.length > 0 && <span className="mono text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full text-white" style={{ background: "var(--mod-accent)" }}>{leads.length} nuovi</span>}
                    </div>
                    <span className="mono text-[11px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Da chiamare entro 24h</span>
                </div>
                {leads.length === 0 ? (
                    <div className="card-tpl p-10 text-center">
                        <UserPlus size={28} className="mx-auto mb-3" style={{ color: "var(--muted-2)" }} />
                        <div className="font-bold" style={{ color: "var(--navy)" }}>Nessun lead in attesa</div>
                        <p className="text-[12.5px] mt-1" style={{ color: "var(--muted)" }}>Quando un tipografo si registra dalla homepage compare qui.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {leads.map((lead) => (
                            <div key={lead.id} data-testid={`lead-${lead.id}`} className="card-tpl p-5">
                                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                                    <div className="flex items-start gap-3">
                                        <div className="w-11 h-11 rounded-lg grid place-items-center text-white font-extrabold text-[14px]" style={{ background: "linear-gradient(135deg, #FF1A5C, #FF6B1A)" }}>
                                            {(lead.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                                        </div>
                                        <div>
                                            <h3 className="font-extrabold text-[16px]" style={{ color: "var(--navy)" }} data-testid={`lead-name-${lead.id}`}>{lead.name}</h3>
                                            <div className="text-[12.5px]" style={{ color: "var(--muted)" }}>
                                                {lead.owner.first_name} {lead.owner.last_name}
                                                {lead.settings.employees && <> · {lead.settings.employees} dipendenti</>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1" style={{ color: "var(--muted)" }}>
                                            <Clock size={11} /> {lead.submitted_at ? new Date(lead.submitted_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
                                        </span>
                                        <button onClick={async () => {
                                            if (!window.confirm("Eliminare questo lead?")) return;
                                            try { await api.delete(`/superadmin/leads/${lead.id}`); loadAll(); } catch (e) { setError(formatApiError(e)); }
                                        }} className="p-1.5 rounded hover:bg-black/5" data-testid={`lead-delete-${lead.id}`}>
                                            <Trash2 size={14} style={{ color: "#B91C1C" }} />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-3 gap-3 text-[12.5px] pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                                    <a href={`tel:${lead.owner.phone_mobile}`} className="flex items-center gap-2 hover:opacity-70" data-testid={`lead-phone-${lead.id}`}>
                                        <Phone size={13} style={{ color: "var(--mod-accent)" }} />
                                        <span className="font-bold">{lead.owner.phone_mobile || "—"}</span>
                                    </a>
                                    <a href={`mailto:${lead.owner.email}`} className="flex items-center gap-2 hover:opacity-70 truncate">
                                        <Mail size={13} style={{ color: "var(--cobalt)" }} />
                                        <span className="font-bold truncate">{lead.owner.email}</span>
                                    </a>
                                    {lead.settings.city && (
                                        <div className="flex items-center gap-2">
                                            <MapPin size={13} style={{ color: "#10B981" }} />
                                            <span className="font-bold">{lead.settings.city}</span>
                                        </div>
                                    )}
                                </div>
                                {lead.lead_info.main_pain && (
                                    <div className="mt-3 p-3 rounded-lg" style={{ background: "var(--bg)", borderLeft: "3px solid var(--mod-accent)" }}>
                                        <div className="mono text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--mod-accent)" }}>Problema principale</div>
                                        <div className="text-[12.5px] italic" style={{ color: "var(--text)" }}>&ldquo;{lead.lead_info.main_pain}&rdquo;</div>
                                    </div>
                                )}
                                {lead.lead_info.how_found && (
                                    <div className="text-[11.5px] mt-2" style={{ color: "var(--muted)" }}>
                                        <strong>Come ci ha conosciuto:</strong> {lead.lead_info.how_found}
                                    </div>
                                )}
                                <div className="mt-4 flex gap-2 flex-wrap">
                                    <button onClick={() => { setActivating(lead); setActivateForm({ plan: "pro", billing_cycle: "annual", setup_fee_paid: false, send_email: true }); setActivateResult(null); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-extrabold text-white" style={{ background: "linear-gradient(135deg, #10B981, #059669)" }} data-testid={`lead-activate-${lead.id}`}>
                                        <Zap size={12} /> Attiva tenant
                                    </button>
                                    <button onClick={() => downloadContract(lead.id, "pro", "annual")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }} data-testid={`lead-contract-${lead.id}`}>
                                        <FileText size={12} /> Anteprima contratto
                                    </button>
                                    <span className="mono text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded ml-auto self-center" style={{ background: "#FEF3C7", color: "#92400E" }}>
                                        ⏳ Da contattare
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Tenants */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-[18px] font-extrabold" style={{ color: "var(--navy)" }}>Tenants registrati</h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={async () => {
                                setError(""); setSuccess("");
                                if (!window.confirm("Ricreare il tenant di test? L'esistente verrà cancellato.")) return;
                                try {
                                    const { data } = await api.post("/superadmin/seed-test-tenant");
                                    setSuccess(`Tenant test pronto. Email: ${data.credentials.email} · Password: ${data.credentials.password}`);
                                    loadAll();
                                } catch (e) { setError(formatApiError(e)); }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold"
                            style={{ background: "rgba(255,107,26,0.12)", color: "#FF6B1A", border: "1px solid rgba(255,107,26,0.3)" }}
                            data-testid="superadmin-seed-test"
                            title="Crea / ricrea un tenant di test fisso con credenziali deterministiche"
                        >
                            <Zap size={13} /> Seed tenant di test
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                setError(""); setSuccess("");
                                try {
                                    const { data } = await api.post("/superadmin/seed-demo-data");
                                    setSuccess(`Demo data ricreato: ${data.customers} clienti, ${data.jobs} commesse, ${data.stages} fasi workflow.`);
                                    loadAll();
                                } catch (e) { setError(formatApiError(e)); }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold"
                            style={{ background: "rgba(124,58,237,0.12)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.3)" }}
                            data-testid="superadmin-seed-demo"
                            title="Popola il tenant di test con 5 clienti + 8 commesse + file demo"
                        >
                            <RefreshCw size={13} /> Reset demo data
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateTenant(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-extrabold text-white"
                            style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" }}
                            data-testid="superadmin-create-tenant-btn"
                        >
                            <UserPlus size={13} /> Crea tenant
                        </button>
                        <span className="mono text-[11px] uppercase tracking-wider font-bold ml-2" style={{ color: "var(--muted)" }}>{tenants.length} totali</span>
                    </div>
                </div>

                {tenants.length === 0 ? (
                    <div className="card-tpl p-12 text-center">
                        <Building2 size={28} className="mx-auto mb-3" style={{ color: "var(--muted-2)" }} />
                        <div className="font-bold" style={{ color: "var(--navy)" }}>Nessun tenant registrato</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tenants.map((t) => (
                            <div key={t.id} data-testid={`tenant-${t.id}`} className="card-tpl p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-lg grid place-items-center text-white font-bold text-[12px]" style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" }}>
                                        {t.name.split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[15px] font-extrabold tracking-tight truncate" style={{ color: "var(--navy)" }}>{t.name}</div>
                                        <div className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>/{t.slug}</div>
                                    </div>
                                    <span className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{ background: t.status === "active" ? "var(--green-soft)" : "rgba(239,68,68,.1)", color: t.status === "active" ? "var(--green)" : "var(--red)" }}>
                                        {t.status}
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                                    <div>
                                        <div className="mono text-[9px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>Plan</div>
                                        <div className="text-[14px] font-bold mt-0.5" style={{ color: "var(--navy)" }}>{t.plan}</div>
                                    </div>
                                    <div>
                                        <div className="mono text-[9px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>Utenti</div>
                                        <div className="text-[14px] font-bold mt-0.5" style={{ color: "var(--navy)" }}>{t.user_count}</div>
                                    </div>
                                    <div>
                                        <div className="mono text-[9px] uppercase tracking-wider font-bold" style={{ color: "var(--muted)" }}>Commesse</div>
                                        <div className="text-[14px] font-bold mt-0.5" style={{ color: "var(--navy)" }}>{t.job_count}</div>
                                    </div>
                                </div>
                                <button onClick={() => toggleTenantUsers(t.id)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-bold text-[12.5px] transition" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }} data-testid={`tenant-users-toggle-${t.id}`}>
                                    <Users size={13} /> {expandedTenant === t.id ? <>Nascondi accessi <ChevronUp size={13} /></> : <>Mostra accessi <ChevronDown size={13} /></>}
                                </button>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button onClick={() => downloadContract(t.id, t.plan === "trial" ? "pro" : t.plan, "annual")} className="inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[11.5px] font-bold" style={{ background: "var(--bg)", color: "var(--navy)", border: "1px solid var(--line)" }} data-testid={`tenant-contract-${t.id}`}>
                                        <FileText size={12} /> Contratto PDF
                                    </button>
                                    <button onClick={() => exportTenantZip(t.id)} className="inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[11.5px] font-bold" style={{ background: "var(--bg)", color: "var(--navy)", border: "1px solid var(--line)" }} data-testid={`tenant-zip-${t.id}`}>
                                        <Download size={12} /> Export ZIP
                                    </button>
                                </div>
                                {expandedTenant === t.id && (
                                    <div className="mt-3 space-y-2" data-testid={`tenant-users-${t.id}`}>
                                        {expandedUsers.length === 0 ? (
                                            <div className="text-[12px] italic text-center py-2" style={{ color: "var(--muted)" }}>Caricamento...</div>
                                        ) : (
                                            expandedUsers.map(u => (
                                                <div key={u.id} className="p-2.5 rounded-lg flex items-center gap-2 text-[12px]" style={{ background: "var(--bg)" }}>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-extrabold truncate" style={{ color: "var(--navy)" }}>{u.first_name} {u.last_name}</div>
                                                        <div className="mono text-[10px] truncate" style={{ color: "var(--muted)" }}>{u.email} · {u.role}</div>
                                                    </div>
                                                    <button onClick={() => impersonate(u.id, u.email)} className="mono text-[10px] uppercase tracking-wider font-extrabold px-2.5 py-1.5 rounded text-white whitespace-nowrap" style={{ background: "var(--mod-accent)" }} data-testid={`impersonate-${u.id}`} title={`Accedi come ${u.email}`}>
                                                        <LogIn size={11} className="inline mr-1" /> Entra
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Activate Lead Modal */}
            {activating && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(10,23,48,0.7)" }} onClick={() => !activateResult && setActivating(null)} data-testid="activate-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-7" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
                        {!activateResult ? (
                            <form onSubmit={submitActivate}>
                                <h2 className="text-[22px] font-extrabold mb-1" style={{ color: "var(--navy)" }}>Attiva tenant</h2>
                                <p className="text-[13px] mb-5" style={{ color: "var(--text-2)" }}>Stai per creare l&apos;account di <b>{activating.name}</b>. Verrà generata una password sicura e (se attivato) inviata via email.</p>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block mono text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "var(--muted)" }}>Piano</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { id: "starter", n: "Starter", p: "€49" },
                                                { id: "pro", n: "Pro", p: "€99", hot: true },
                                                { id: "business", n: "Business", p: "€199" },
                                            ].map(p => (
                                                <button key={p.id} type="button" onClick={() => setActivateForm(prev => ({ ...prev, plan: p.id }))} className="p-3 rounded-lg text-center transition" style={{ background: activateForm.plan === p.id ? "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" : "var(--bg)", color: activateForm.plan === p.id ? "#fff" : "var(--navy)", border: activateForm.plan === p.id ? "2px solid var(--mod-accent)" : "1px solid var(--line)" }} data-testid={`plan-${p.id}`}>
                                                    <div className="font-extrabold text-[13px]">{p.n}</div>
                                                    <div className="text-[11px] mt-0.5 opacity-80">{p.p}/mese</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block mono text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "var(--muted)" }}>Ciclo di fatturazione</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button type="button" onClick={() => setActivateForm(prev => ({ ...prev, billing_cycle: "monthly" }))} className="p-2.5 rounded-lg text-[12.5px] font-bold transition" style={{ background: activateForm.billing_cycle === "monthly" ? "var(--navy)" : "var(--bg)", color: activateForm.billing_cycle === "monthly" ? "#fff" : "var(--navy)" }} data-testid="cycle-monthly">Mensile</button>
                                            <button type="button" onClick={() => setActivateForm(prev => ({ ...prev, billing_cycle: "annual" }))} className="p-2.5 rounded-lg text-[12.5px] font-bold transition" style={{ background: activateForm.billing_cycle === "annual" ? "var(--navy)" : "var(--bg)", color: activateForm.billing_cycle === "annual" ? "#fff" : "var(--navy)" }} data-testid="cycle-annual">Annuale -20%</button>
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                                        <input type="checkbox" checked={activateForm.setup_fee_paid} onChange={(e) => setActivateForm(prev => ({ ...prev, setup_fee_paid: e.target.checked }))} data-testid="setup-fee-paid" />
                                        <span>Setup fee €199 già pagato</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                                        <input type="checkbox" checked={activateForm.send_email} onChange={(e) => setActivateForm(prev => ({ ...prev, send_email: e.target.checked }))} data-testid="send-email" />
                                        <span>Invia automaticamente email con credenziali</span>
                                    </label>
                                </div>
                                <div className="mt-6 flex gap-2 justify-end">
                                    <button type="button" onClick={() => setActivating(null)} className="px-4 py-2 rounded-lg text-[13px] font-bold" style={{ background: "var(--bg)", color: "var(--navy)" }}>Annulla</button>
                                    <button type="submit" className="px-5 py-2 rounded-lg text-[13px] font-extrabold text-white" style={{ background: "linear-gradient(135deg, #10B981, #059669)" }} data-testid="activate-submit">
                                        Attiva e crea account
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div data-testid="activate-result">
                                <div className="w-14 h-14 rounded-full mx-auto mb-3 grid place-items-center" style={{ background: "#10B98115" }}>
                                    <Check size={28} style={{ color: "#10B981" }} />
                                </div>
                                <h2 className="text-[22px] font-extrabold text-center mb-1" style={{ color: "var(--navy)" }}>Tenant attivato!</h2>
                                <p className="text-[13px] text-center mb-5" style={{ color: "var(--text-2)" }}>
                                    {activateResult.email_sent ? "✉️ Email credenziali inviata al cliente." : activateResult.email_error ? `⚠️ Email NON inviata (${activateResult.email_error.substring(0,80)}). Copia le credenziali e mandagliele a mano.` : "Email NON inviata (per tua scelta). Copia le credenziali."}
                                </p>
                                <div className="p-4 rounded-xl mb-4" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
                                    <div className="mb-3">
                                        <div className="mono text-[10px] uppercase tracking-wider font-bold mb-0.5" style={{ color: "var(--muted)" }}>Email</div>
                                        <div className="mono font-extrabold text-[13.5px]" style={{ color: "var(--navy)" }} data-testid="result-email">{activateResult.credentials.email}</div>
                                    </div>
                                    <div className="pt-3" style={{ borderTop: "1px solid var(--line)" }}>
                                        <div className="mono text-[10px] uppercase tracking-wider font-bold mb-0.5" style={{ color: "var(--muted)" }}>Password temporanea</div>
                                        <div className="flex items-center gap-2">
                                            <code className="mono font-extrabold text-[16px]" style={{ color: "var(--mod-accent)" }} data-testid="result-password">{activateResult.credentials.password}</code>
                                            <button type="button" onClick={() => navigator.clipboard.writeText(`Email: ${activateResult.credentials.email}\nPassword: ${activateResult.credentials.password}\nLogin: ${window.location.origin}/login`)} className="p-1.5 rounded hover:bg-black/5" data-testid="copy-creds"><Copy size={14} style={{ color: "var(--navy)" }} /></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => { downloadContract(activateResult.tenant_id, activateForm.plan, activateForm.billing_cycle); }} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-bold" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }} data-testid="download-contract-after">
                                        <FileText size={13} /> Scarica contratto PDF
                                    </button>
                                    <button onClick={() => { setActivating(null); setActivateResult(null); }} className="px-5 py-2.5 rounded-lg text-[13px] font-extrabold text-white" style={{ background: "var(--navy)" }}>Chiudi</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* CREATE TENANT MODAL */}
            {showCreateTenant && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(10,23,48,0.6)" }} onClick={() => setShowCreateTenant(false)}>
                    <div className="card-tpl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="create-tenant-modal">
                        <div className="flex items-center gap-2 mb-1">
                            <UserPlus size={20} style={{ color: "var(--mod-accent)" }} />
                            <h2 className="text-[20px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>Crea tenant</h2>
                        </div>
                        <p className="text-[12.5px] mb-5" style={{ color: "var(--text-2)" }}>
                            Crea direttamente una tipografia + account admin. Salta il flusso lead. La password è generata casualmente.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Nome tipografia *</label>
                                <input className="input" value={createForm.tenant_name} onChange={(e) => setCreateForm({ ...createForm, tenant_name: e.target.value })} placeholder="Es. Stamperia Alpha" data-testid="ct-name" />
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Nome admin *</label>
                                <input className="input" value={createForm.admin_first_name} onChange={(e) => setCreateForm({ ...createForm, admin_first_name: e.target.value })} data-testid="ct-first" />
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Cognome admin *</label>
                                <input className="input" value={createForm.admin_last_name} onChange={(e) => setCreateForm({ ...createForm, admin_last_name: e.target.value })} data-testid="ct-last" />
                            </div>
                            <div className="col-span-2">
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Email admin *</label>
                                <input type="email" className="input" value={createForm.admin_email} onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })} placeholder="admin@nuova-tipografia.it" data-testid="ct-email" />
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Telefono</label>
                                <input className="input" value={createForm.phone_mobile} onChange={(e) => setCreateForm({ ...createForm, phone_mobile: e.target.value })} placeholder="+39 333 123 4567" />
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>P. IVA</label>
                                <input className="input" value={createForm.vat} onChange={(e) => setCreateForm({ ...createForm, vat: e.target.value })} />
                            </div>
                            <div className="col-span-2">
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Città</label>
                                <input className="input" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} placeholder="Milano" />
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Piano *</label>
                                <select className="input" value={createForm.plan} onChange={(e) => setCreateForm({ ...createForm, plan: e.target.value })} data-testid="ct-plan">
                                    <option value="starter">Starter</option>
                                    <option value="pro">Pro</option>
                                    <option value="business">Business</option>
                                </select>
                            </div>
                            <div>
                                <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1 block" style={{ color: "var(--muted)" }}>Ciclo *</label>
                                <select className="input" value={createForm.billing_cycle} onChange={(e) => setCreateForm({ ...createForm, billing_cycle: e.target.value })}>
                                    <option value="annual">Annuale</option>
                                    <option value="monthly">Mensile</option>
                                </select>
                            </div>
                            <div className="col-span-2 flex items-center gap-2 mt-1">
                                <input type="checkbox" id="ct-send" checked={createForm.send_email} onChange={(e) => setCreateForm({ ...createForm, send_email: e.target.checked })} />
                                <label htmlFor="ct-send" className="text-[12.5px]" style={{ color: "var(--text-2)" }}>Invia email di benvenuto con credenziali</label>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={() => setShowCreateTenant(false)} className="flex-1 px-4 py-2.5 rounded-lg text-[13px] font-bold" style={{ background: "var(--bg)", border: "1px solid var(--line)", color: "var(--navy)" }}>Annulla</button>
                            <button
                                onClick={submitCreateTenant}
                                disabled={creating || !createForm.tenant_name || !createForm.admin_first_name || !createForm.admin_last_name || !createForm.admin_email}
                                className="flex-1 px-4 py-2.5 rounded-lg text-[13px] font-extrabold text-white disabled:opacity-50"
                                style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))" }}
                                data-testid="ct-submit"
                            >
                                {creating ? "Creazione..." : "Crea tenant"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AriaEcosystemPanel />
            <AriaHubTrackingPanel />
            <EcosystemAffinityPanel />
        </div>
    );
}

/**
 * AriaEcosystemPanel — Phase 6 Blocco 5
 * Aggregator superadmin: installations, leads, plan intents, security log.
 */
function AriaEcosystemPanel() {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [err, setErr] = React.useState("");

    React.useEffect(() => {
        (async () => {
            try {
                const { api } = await import("@/lib/api");
                const { data } = await api.get("/widget/superadmin/ecosystem-overview");
                setData(data);
            } catch (e) {
                setErr("Errore caricamento Aria overview");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="card-tpl p-6 mt-6 text-[13px]" style={{ color: "var(--text-2)" }}>Caricamento Aria Ecosystem…</div>;
    if (err) return <div className="card-tpl p-6 mt-6 text-[13px]" style={{ color: "var(--red)" }}>{err}</div>;
    if (!data) return null;

    const t = data.totals || {};
    const KPI = ({ label, value, accent }) => (
        <div className="card-tpl p-4">
            <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">{label}</div>
            <div className="text-[26px] font-extrabold tracking-tight" style={{ color: accent || "var(--navy)" }}>{value}</div>
        </div>
    );

    return (
        <div className="mt-8" data-testid="aria-ecosystem-panel">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-65">aria ecosystem control center</div>
                    <h2 className="tracking-tight mt-1" style={{ color: "var(--navy)", fontFamily: "Geist, Outfit, sans-serif", fontSize: "1.55rem", fontWeight: 200, letterSpacing: "-0.025em" }}>
                        Aria globale. Tutto l&apos;ecosistema, una vista.
                    </h2>
                </div>
                <div className="mono text-[10.5px] opacity-50">aggiornato {new Date(data.generated_at).toLocaleTimeString("it-IT")}</div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <KPI label="Installazioni" value={t.installations || 0} />
                <KPI label="Lead totali" value={t.leads_total || 0} accent="var(--mod-accent)" />
                <KPI label="Lead 30gg" value={t.leads_30d || 0} accent="var(--green)" />
                <KPI label="Upgrade intent" value={t.plan_intents_pending || 0} accent="#7C3AED" />
                <KPI label="Eventi security 30gg" value={t.security_events_30d || 0} accent={t.security_events_30d > 0 ? "var(--red)" : "var(--text-2)"} />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Plan distribution */}
                <div className="card-tpl p-5">
                    <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">Distribuzione piani</div>
                    {Object.entries(data.plan_distribution || {}).map(([plan, n]) => (
                        <div key={plan} className="flex items-center justify-between py-1.5 text-[13px]">
                            <span className="capitalize">{plan}</span>
                            <span className="mono font-bold">{n}</span>
                        </div>
                    ))}
                    <div className="mt-4 mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-2">Modalità Experience attive</div>
                    {Object.entries(data.mode_distribution || {}).map(([mode, n]) => (
                        <div key={mode} className="flex items-center justify-between py-1.5 text-[13px]">
                            <span className="capitalize">{mode}</span>
                            <span className="mono font-bold">{n}</span>
                        </div>
                    ))}
                </div>

                {/* Security */}
                <div className="card-tpl p-5">
                    <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">Eventi security 30gg per tipo</div>
                    {Object.keys(data.security_by_kind_30d || {}).length === 0 ? (
                        <div className="text-[13px] opacity-65">Nessun evento (perfetto 🛡️)</div>
                    ) : (
                        Object.entries(data.security_by_kind_30d || {}).map(([k, n]) => (
                            <div key={k} className="flex items-center justify-between py-1.5 text-[13px]">
                                <span>{k}</span>
                                <span className="mono font-bold" style={{ color: "var(--red)" }}>{n}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Top installations */}
            <div className="card-tpl p-5 mt-4">
                <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">Top installazioni per lead (30gg)</div>
                {(data.top_installations_30d || []).length === 0 ? (
                    <div className="text-[13px] opacity-65">Nessun lead negli ultimi 30gg.</div>
                ) : (
                    <table className="w-full text-[12.5px]">
                        <thead>
                            <tr className="text-left mono uppercase text-[10px] opacity-65">
                                <th className="py-2">Sito</th>
                                <th>Modalità</th>
                                <th>Piano</th>
                                <th className="text-right">Lead 30gg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_installations_30d.map((row) => (
                                <tr key={row.widget_token} className="border-t" style={{ borderColor: "var(--border)" }}>
                                    <td className="py-2">{row.site_name || "—"}</td>
                                    <td className="capitalize">{row.experience_mode}</td>
                                    <td className="capitalize">{row.plan_tier}</td>
                                    <td className="text-right mono font-bold">{row.leads_30d}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pending plan intents */}
            <div className="card-tpl p-5 mt-4">
                <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">Upgrade intent in attesa</div>
                {(data.pending_plan_intents || []).length === 0 ? (
                    <div className="text-[13px] opacity-65">Nessun upgrade intent pending.</div>
                ) : (
                    data.pending_plan_intents.map((it) => (
                        <div key={it._id} className="py-2 text-[12.5px] border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
                            <div>
                                <div className="font-bold">{it.site_name || it.widget_token}</div>
                                <div className="opacity-65">vuole <b>{it.requested_mode}</b> (ora: {it.current_plan})</div>
                            </div>
                            <div className="mono text-[10px] opacity-50">{new Date(it.created_at).toLocaleString("it-IT")}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

/**
 * AriaHubTrackingPanel — Phase 6.2
 * Smart-Consent tracking dashboard:
 *  - Conversazioni Hub (Level 1 anonimo + Level 2 identificato via consent)
 *  - Learning Dataset aggregato (intent/category/future_needs patterns)
 */
function AriaHubTrackingPanel() {
    const [activeTab, setActiveTab] = React.useState("conversations"); // conversations | learning
    const [sessions, setSessions] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [learning, setLearning] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState("");
    const [detail, setDetail] = React.useState(null);
    const [filters, setFilters] = React.useState({ outcome: "", has_lead: "", country: "" });
    const [days, setDays] = React.useState(30);

    const load = React.useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const { api } = await import("@/lib/api");
            if (activeTab === "conversations") {
                const params = new URLSearchParams({ limit: "50" });
                if (filters.outcome) params.set("outcome", filters.outcome);
                if (filters.has_lead) params.set("has_lead", filters.has_lead);
                if (filters.country) params.set("country", filters.country);
                const { data } = await api.get(`/superadmin/aria-hub/sessions?${params.toString()}`);
                setSessions(data.items || []);
                setTotal(data.total || 0);
            } else {
                const { data } = await api.get(`/superadmin/aria-hub/learning?days=${days}`);
                setLearning(data);
            }
        } catch (e) {
            setErr("Errore caricamento");
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters, days]);

    React.useEffect(() => { load(); }, [load]);

    const openDetail = async (sessionId) => {
        try {
            const { api } = await import("@/lib/api");
            const { data } = await api.get(`/superadmin/aria-hub/sessions/${sessionId}`);
            setDetail(data);
        } catch (e) { setErr("Dettaglio non disponibile"); }
    };

    const intentColor = (lvl) => lvl === "hot" ? "#FB7185" : lvl === "warm" ? "#FBBF24" : lvl === "cold" ? "#60A5FA" : "var(--text-2)";

    return (
        <div className="mt-8" data-testid="aria-hub-tracking-panel">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-65">aria hub — smart-consent tracking</div>
                    <h2 className="tracking-tight mt-1" style={{ color: "var(--navy)", fontFamily: "Geist, Outfit, sans-serif", fontSize: "1.55rem", fontWeight: 200, letterSpacing: "-0.025em" }}>
                        Conversazioni hub & Learning Dataset
                    </h2>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab("conversations")}
                        className={`px-3 py-1.5 rounded-full text-[12px] mono uppercase tracking-wider transition ${activeTab === "conversations" ? "" : "opacity-50"}`}
                        style={{ background: activeTab === "conversations" ? "var(--navy)" : "transparent", color: activeTab === "conversations" ? "white" : "var(--text-2)", border: "1px solid var(--border)" }}
                        data-testid="hub-tab-conversations"
                    >Conversazioni</button>
                    <button
                        onClick={() => setActiveTab("learning")}
                        className={`px-3 py-1.5 rounded-full text-[12px] mono uppercase tracking-wider transition ${activeTab === "learning" ? "" : "opacity-50"}`}
                        style={{ background: activeTab === "learning" ? "var(--navy)" : "transparent", color: activeTab === "learning" ? "white" : "var(--text-2)", border: "1px solid var(--border)" }}
                        data-testid="hub-tab-learning"
                    >Learning Dataset</button>
                </div>
            </div>

            {err && <div className="card-tpl p-4 mb-3 text-[12.5px]" style={{ color: "var(--red)" }}>{err}</div>}

            {/* === CONVERSATIONS TAB === */}
            {activeTab === "conversations" && (
                <div className="card-tpl p-5">
                    <div className="flex gap-2 mb-3 flex-wrap">
                        <select
                            className="px-3 py-1.5 text-[12px] rounded border"
                            style={{ borderColor: "var(--border)", background: "white" }}
                            value={filters.outcome}
                            onChange={(e) => setFilters({ ...filters, outcome: e.target.value })}
                            data-testid="hub-filter-outcome"
                        >
                            <option value="">Tutti gli esiti</option>
                            <option value="converted">Convertiti</option>
                            <option value="abandoned">Abbandonati</option>
                            <option value="no_match">No-match</option>
                        </select>
                        <select
                            className="px-3 py-1.5 text-[12px] rounded border"
                            style={{ borderColor: "var(--border)", background: "white" }}
                            value={filters.has_lead}
                            onChange={(e) => setFilters({ ...filters, has_lead: e.target.value })}
                            data-testid="hub-filter-lead"
                        >
                            <option value="">Con/senza lead</option>
                            <option value="true">Solo con lead</option>
                            <option value="false">Solo anonimi</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Country (IT, FR…)"
                            value={filters.country}
                            onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                            className="px-3 py-1.5 text-[12px] rounded border w-32"
                            style={{ borderColor: "var(--border)" }}
                            data-testid="hub-filter-country"
                        />
                        <div className="mono text-[10.5px] opacity-50 self-center">Totale filtro: {total}</div>
                    </div>

                    {loading ? <div className="text-[13px] opacity-65 py-3">Caricamento…</div> : (
                        <table className="w-full text-[12.5px]" data-testid="hub-sessions-table">
                            <thead>
                                <tr className="text-left mono uppercase text-[10px] opacity-65">
                                    <th className="py-2">Inizio</th>
                                    <th>Intent</th>
                                    <th>Categoria</th>
                                    <th>Path</th>
                                    <th>Country</th>
                                    <th>Msg</th>
                                    <th>Esito</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.length === 0 && <tr><td colSpan={8} className="py-4 opacity-65 text-[12.5px]">Nessuna sessione.</td></tr>}
                                {sessions.map((s) => (
                                    <tr key={s.session_id} className="border-t" style={{ borderColor: "var(--border)" }}>
                                        <td className="py-2 mono text-[11px]">{s.started_at ? new Date(s.started_at).toLocaleString("it-IT") : "—"}</td>
                                        <td className="font-bold capitalize" style={{ color: intentColor(s.intent_level) }}>{s.intent_level || "—"}</td>
                                        <td>{s.category || "—"}</td>
                                        <td className="truncate max-w-[180px] mono text-[10.5px] opacity-75">{s.first_path}</td>
                                        <td className="mono text-[10.5px]">{s.country_iso2 || "??"}</td>
                                        <td className="mono">{s.message_count}</td>
                                        <td>
                                            {s.outcome === "converted" ? (
                                                <span style={{ color: "var(--green)" }}>✓ Lead</span>
                                            ) : (
                                                <span className="opacity-60">{s.outcome || "in corso"}</span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => openDetail(s.session_id)}
                                                className="text-[11px] underline opacity-75 hover:opacity-100"
                                                data-testid={`hub-session-detail-${s.session_id}`}
                                            >Apri</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* === LEARNING DATASET TAB === */}
            {activeTab === "learning" && (
                <div className="space-y-4">
                    <div className="flex gap-2 items-center">
                        <span className="mono text-[10.5px] uppercase tracking-wider opacity-65">Finestra temporale:</span>
                        {[7, 30, 90, 180].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`px-3 py-1 rounded-full text-[11.5px] mono ${days === d ? "" : "opacity-55"}`}
                                style={{ background: days === d ? "var(--navy)" : "transparent", color: days === d ? "white" : "var(--text-2)", border: "1px solid var(--border)" }}
                                data-testid={`hub-learning-window-${d}`}
                            >{d}gg</button>
                        ))}
                    </div>

                    {loading || !learning ? <div className="text-[13px] opacity-65">Caricamento…</div> : (
                        <>
                            {/* KPI */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Sessioni</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>{learning.totals?.sessions || 0}</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Convertite</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--green)" }}>{learning.totals?.converted || 0}</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Conversion %</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--mod-accent)" }}>{learning.totals?.conversion_rate_pct || 0}%</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Msg medi/sess.</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>{learning.totals?.avg_messages_per_session || 0}</div>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <LearningList title="Top categorie business" items={learning.top_categories} />
                                <LearningList title="Top future needs (cross-sell)" items={learning.top_future_needs} />
                                <LearningList title="Top tag conversazioni" items={learning.top_tags} />
                                <LearningList title="Top entry path" items={learning.top_entry_paths} mono />
                            </div>

                            <div className="grid md:grid-cols-3 gap-4">
                                <DistroCard title="Intent" items={learning.intent_distribution} />
                                <DistroCard title="Outcome" items={learning.outcome_distribution} />
                                <DistroCard title="Sentiment" items={learning.sentiment_distribution} />
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* === DETAIL MODAL === */}
            {detail && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="card-tpl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto" data-testid="hub-session-detail-modal">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <div className="mono text-[10px] uppercase tracking-wider opacity-65">Sessione</div>
                                <div className="text-[14px] font-bold mono break-all">{detail.session_id}</div>
                                <div className="text-[11.5px] opacity-65 mt-1">{detail.first_path} · {detail.country_iso2 || "??"} · {detail.message_count} msg</div>
                            </div>
                            <button onClick={() => setDetail(null)} className="opacity-65 hover:opacity-100 text-[18px]">×</button>
                        </div>
                        {detail.summary && <div className="text-[12.5px] italic opacity-75 mb-3 border-l-2 pl-3" style={{ borderColor: "var(--mod-accent)" }}>{detail.summary}</div>}
                        {detail.lead && (
                            <div className="card-tpl p-3 mb-3 bg-emerald-50/40" style={{ borderColor: "var(--green)" }}>
                                <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Lead identificato</div>
                                <div className="text-[13px] font-bold">{detail.lead.first_name} {detail.lead.last_name}</div>
                                <div className="text-[12px]">{detail.lead.email} · {detail.lead.phone}</div>
                                <div className="text-[10.5px] opacity-65 mt-1">Consenso: {detail.lead.consent_at} (v.{detail.lead.consent_text_version}){detail.lead.marketing_consent ? " + marketing" : ""}</div>
                            </div>
                        )}
                        <div className="space-y-2">
                            {(detail.messages || []).map((m, i) => (
                                <div key={i} className={`text-[12.5px] p-2 rounded ${m.role === "user" ? "bg-blue-50/40 ml-8" : "bg-gray-50 mr-8"}`}>
                                    <div className="mono text-[9.5px] opacity-50 mb-0.5 uppercase">{m.role}</div>
                                    <div className="whitespace-pre-wrap">{m.content}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function LearningList({ title, items, mono }) {
    return (
        <div className="card-tpl p-5">
            <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">{title}</div>
            {(!items || items.length === 0) ? (
                <div className="text-[12.5px] opacity-65">Nessun dato ancora.</div>
            ) : items.slice(0, 10).map(([label, n]) => (
                <div key={label} className="flex items-center justify-between py-1 text-[12.5px] border-t" style={{ borderColor: "var(--border)" }}>
                    <span className={mono ? "mono text-[11.5px]" : "capitalize"}>{label}</span>
                    <span className="mono font-bold">{n}</span>
                </div>
            ))}
        </div>
    );
}

function DistroCard({ title, items }) {
    const entries = Object.entries(items || {});
    const total = entries.reduce((sum, [, n]) => sum + n, 0);
    return (
        <div className="card-tpl p-4">
            <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-2">{title}</div>
            {entries.length === 0 ? (
                <div className="text-[12.5px] opacity-65">—</div>
            ) : entries.map(([k, v]) => (
                <div key={k} className="text-[12px] py-0.5 flex items-center justify-between">
                    <span className="capitalize">{k}</span>
                    <span className="mono font-bold">{v} <span className="opacity-50">({total ? Math.round((v / total) * 100) : 0}%)</span></span>
                </div>
            ))}
        </div>
    );
}


/**
 * EcosystemAffinityPanel — Phase 6.3
 * Cross-Promo Network: gestisce le regole di affinità tra categorie business.
 * Mostra anche il grafo del network (chi suggerisce a chi).
 */
function EcosystemAffinityPanel() {
    const [activeTab, setActiveTab] = React.useState("rules");
    const [rules, setRules] = React.useState([]);
    const [cacheSize, setCacheSize] = React.useState(0);
    const [graph, setGraph] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [err, setErr] = React.useState("");
    const [newRule, setNewRule] = React.useState({ category_a: "", category_b: "", weight: 7, reason: "" });
    const [busyAdd, setBusyAdd] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const { api } = await import("@/lib/api");
            if (activeTab === "rules") {
                const { data } = await api.get("/superadmin/ecosystem/affinity");
                setRules(data.items || []);
                setCacheSize(data.cache_size || 0);
            } else {
                const { data } = await api.get("/superadmin/ecosystem/network-graph");
                setGraph(data);
            }
        } catch (e) {
            setErr("Errore caricamento ecosystem");
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    React.useEffect(() => { load(); }, [load]);

    const addRule = async () => {
        if (!newRule.category_a.trim() || !newRule.category_b.trim()) {
            setErr("Compila entrambe le categorie"); return;
        }
        setBusyAdd(true); setErr("");
        try {
            const { api } = await import("@/lib/api");
            await api.post("/superadmin/ecosystem/affinity", {
                category_a: newRule.category_a.trim().toLowerCase().replace(/\s+/g, "_"),
                category_b: newRule.category_b.trim().toLowerCase().replace(/\s+/g, "_"),
                weight: parseInt(newRule.weight, 10),
                reason: newRule.reason.trim(),
            });
            setNewRule({ category_a: "", category_b: "", weight: 7, reason: "" });
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore creazione affinità");
        } finally {
            setBusyAdd(false);
        }
    };

    const toggleActive = async (id, active) => {
        try {
            const { api } = await import("@/lib/api");
            await api.put(`/superadmin/ecosystem/affinity/${id}`, { active: !active });
            await load();
        } catch (e) { setErr("Errore toggle"); }
    };

    const deleteRule = async (id) => {
        if (!window.confirm("Eliminare questa regola di affinità?")) return;
        try {
            const { api } = await import("@/lib/api");
            await api.delete(`/superadmin/ecosystem/affinity/${id}`);
            await load();
        } catch (e) { setErr("Errore eliminazione"); }
    };

    const reseed = async () => {
        if (!window.confirm("Riaggiungere le regole default mancanti? (non rimuove quelle custom)")) return;
        try {
            const { api } = await import("@/lib/api");
            const { data } = await api.post("/superadmin/ecosystem/affinity/seed");
            alert(`Aggiunte ${data.added} regole. Totale ora: ${data.total}.`);
            await load();
        } catch (e) { setErr("Errore reseed"); }
    };

    return (
        <div className="mt-8" data-testid="ecosystem-affinity-panel">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-65">ecosystem cross-promo</div>
                    <h2 className="tracking-tight mt-1" style={{ color: "var(--navy)", fontFamily: "Geist, Outfit, sans-serif", fontSize: "1.55rem", fontWeight: 200, letterSpacing: "-0.025em" }}>
                        Network di affinità categorie
                    </h2>
                    <div className="text-[12px] opacity-65 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Mappa di complementarietà tra business. Boost lo scoring Aria quando l&apos;utente cerca un servizio complementare a quello del sito ospitante.
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab("rules")}
                        className={`px-3 py-1.5 rounded-full text-[12px] mono uppercase tracking-wider transition ${activeTab === "rules" ? "" : "opacity-50"}`}
                        style={{ background: activeTab === "rules" ? "var(--navy)" : "transparent", color: activeTab === "rules" ? "white" : "var(--text-2)", border: "1px solid var(--border)" }}
                        data-testid="affinity-tab-rules"
                    >Regole ({rules.length})</button>
                    <button
                        onClick={() => setActiveTab("graph")}
                        className={`px-3 py-1.5 rounded-full text-[12px] mono uppercase tracking-wider transition ${activeTab === "graph" ? "" : "opacity-50"}`}
                        style={{ background: activeTab === "graph" ? "var(--navy)" : "transparent", color: activeTab === "graph" ? "white" : "var(--text-2)", border: "1px solid var(--border)" }}
                        data-testid="affinity-tab-graph"
                    >Network Graph</button>
                </div>
            </div>

            {err && <div className="card-tpl p-4 mb-3 text-[12.5px]" style={{ color: "var(--red)" }}>{err}</div>}

            {activeTab === "rules" && (
                <>
                    {/* CREATE FORM */}
                    <div className="card-tpl p-5 mb-3" style={{ background: "white" }}>
                        <div className="mono text-[10px] uppercase tracking-wider font-extrabold opacity-65 mb-3">Nuova regola</div>
                        <div className="grid md:grid-cols-5 gap-2 items-end">
                            <input
                                type="text"
                                placeholder="es. palestra"
                                value={newRule.category_a}
                                onChange={(e) => setNewRule({ ...newRule, category_a: e.target.value })}
                                className="px-3 py-2 text-[13px] rounded border"
                                style={{ borderColor: "var(--border)" }}
                                data-testid="affinity-new-cat-a"
                            />
                            <input
                                type="text"
                                placeholder="es. abbigliamento_sportivo"
                                value={newRule.category_b}
                                onChange={(e) => setNewRule({ ...newRule, category_b: e.target.value })}
                                className="px-3 py-2 text-[13px] rounded border"
                                style={{ borderColor: "var(--border)" }}
                                data-testid="affinity-new-cat-b"
                            />
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={newRule.weight}
                                onChange={(e) => setNewRule({ ...newRule, weight: e.target.value })}
                                className="px-3 py-2 text-[13px] rounded border"
                                style={{ borderColor: "var(--border)" }}
                                placeholder="Peso 1-10"
                                data-testid="affinity-new-weight"
                            />
                            <input
                                type="text"
                                placeholder="Motivo (opzionale)"
                                value={newRule.reason}
                                onChange={(e) => setNewRule({ ...newRule, reason: e.target.value })}
                                className="px-3 py-2 text-[13px] rounded border"
                                style={{ borderColor: "var(--border)" }}
                                data-testid="affinity-new-reason"
                            />
                            <button
                                onClick={addRule}
                                disabled={busyAdd}
                                className="px-4 py-2 rounded-full mono text-[11px] uppercase tracking-wider font-bold"
                                style={{ background: "var(--navy)", color: "white", border: "1px solid var(--navy)" }}
                                data-testid="affinity-create"
                            >
                                {busyAdd ? "..." : "Aggiungi"}
                            </button>
                        </div>
                        <div className="flex items-center justify-between mt-3 text-[11px] opacity-65">
                            <div>Cache attiva: <b>{cacheSize}</b> regole · Le coppie sono simmetriche (a→b == b→a)</div>
                            <button onClick={reseed} className="underline" data-testid="affinity-reseed">Reseed default rules</button>
                        </div>
                    </div>

                    {/* RULES LIST */}
                    <div className="card-tpl p-5">
                        {loading ? <div className="text-[13px] opacity-65">Caricamento…</div> : (
                            <table className="w-full text-[12.5px]" data-testid="affinity-rules-table">
                                <thead>
                                    <tr className="text-left mono uppercase text-[10px] opacity-65">
                                        <th className="py-2">Cat. A</th>
                                        <th></th>
                                        <th>Cat. B</th>
                                        <th>Peso</th>
                                        <th>Motivo</th>
                                        <th>Stato</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rules.length === 0 && <tr><td colSpan={7} className="py-4 opacity-65">Nessuna regola.</td></tr>}
                                    {rules.map((r) => (
                                        <tr key={r.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                                            <td className="py-2 mono text-[11.5px]">{r.category_a}</td>
                                            <td className="opacity-50">↔</td>
                                            <td className="mono text-[11.5px]">{r.category_b}</td>
                                            <td className="mono font-bold" style={{ color: r.weight >= 8 ? "var(--green)" : r.weight >= 5 ? "var(--navy)" : "var(--text-2)" }}>{r.weight}</td>
                                            <td className="opacity-75 text-[11.5px] max-w-[280px] truncate">{r.reason || "—"}</td>
                                            <td>
                                                <button onClick={() => toggleActive(r.id, r.active)} className="text-[11px] underline" data-testid={`affinity-toggle-${r.id}`}>
                                                    {r.active ? "Attiva" : "Disattiva"}
                                                </button>
                                            </td>
                                            <td className="text-right">
                                                <button onClick={() => deleteRule(r.id)} className="text-[11px] opacity-65 hover:opacity-100" style={{ color: "var(--red)" }} data-testid={`affinity-delete-${r.id}`}>×</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {activeTab === "graph" && (
                <>
                    {loading || !graph ? <div className="text-[13px] opacity-65">Caricamento…</div> : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Hosts attivi</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--navy)" }}>{graph.total_hosts}</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Partner attivi</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--mod-accent)" }}>{graph.total_partners}</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Regole caricate</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--green)" }}>{graph.affinity_rules_loaded}</div>
                                </div>
                                <div className="card-tpl p-4">
                                    <div className="mono text-[10px] uppercase tracking-wider opacity-65 mb-1">Categorie orfane</div>
                                    <div className="text-[26px] font-extrabold tracking-tight" style={{ color: graph.orphan_categories.length ? "var(--red)" : "var(--text-2)" }}>{graph.orphan_categories.length}</div>
                                </div>
                            </div>

                            {graph.orphan_categories.length > 0 && (
                                <div className="card-tpl p-4 mb-4" style={{ background: "rgba(251,114,133,0.07)", borderColor: "var(--red)" }}>
                                    <div className="mono text-[10.5px] uppercase tracking-wider font-extrabold mb-2" style={{ color: "var(--red)" }}>⚠ Partner senza affinità definita</div>
                                    <div className="text-[12.5px] opacity-75 mb-2">Questi partner sono nel network ma nessuna regola li collega a un host → non verranno mai suggeriti.</div>
                                    {graph.orphan_categories.map((o) => (
                                        <div key={o.category} className="text-[12.5px] py-0.5 mono">▢ {o.category} ({o.n_partners} partner)</div>
                                    ))}
                                </div>
                            )}

                            {graph.graph.length === 0 ? (
                                <div className="card-tpl p-5 text-[12.5px] opacity-65">Nessun host attivo nel network al momento.</div>
                            ) : graph.graph.map((host) => (
                                <div key={host.host_token} className="card-tpl p-5 mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <div className="text-[14px] font-bold" style={{ color: "var(--navy)" }}>{host.host_name}</div>
                                            <div className="mono text-[10.5px] opacity-65">{host.host_category}{host.host_city ? ` · ${host.host_city}` : ""}</div>
                                        </div>
                                        <div className="mono text-[10px] uppercase tracking-wider opacity-50">→ può proporre</div>
                                    </div>
                                    {host.suggested_partners.length === 0 ? (
                                        <div className="text-[12.5px] opacity-50">Nessun partner suggeribile con le regole attuali.</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {host.suggested_partners.map((p, i) => (
                                                <div key={i} className="px-3 py-1.5 rounded-full text-[11.5px]" style={{ background: "rgba(0,0,0,0.04)", border: "1px solid var(--border)" }}>
                                                    <span className="mono opacity-75">{p.category}</span>
                                                    <span className="opacity-50"> · </span>
                                                    <span className="font-bold">{p.site_name}</span>
                                                    <span className="opacity-50"> · w={p.affinity_weight}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════════════
// ForgeQuickLaunch — pulsante rapido nella console Superadmin per entrare
// in FORGE Cognitive OS. L'URL è editabile (persistito in localStorage)
// così puoi puntare a diverse installazioni (locale, VPS, staging).
// ═══════════════════════════════════════════════════════════════════════
function ForgeQuickLaunch() {
    const STORAGE_KEY = "superadmin:forge_url";
    const DEFAULT_URL = "/forge";
    const [url, setUrl] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_URL; }
        catch (e) { return DEFAULT_URL; }
    });
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(url);

    const save = () => {
        const clean = (draft || "").trim() || DEFAULT_URL;
        try { localStorage.setItem(STORAGE_KEY, clean); } catch (e) {}
        setUrl(clean);
        setEditing(false);
    };
    const reset = () => {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        setUrl(DEFAULT_URL);
        setDraft(DEFAULT_URL);
        setEditing(false);
    };
    const openForge = () => {
        // Same-origin path (/forge) → navigazione interna
        // URL assoluto → apre in nuova tab (installazione esterna)
        if (url.startsWith("http://") || url.startsWith("https://")) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            window.location.href = url;
        }
    };

    return (
        <div className="card-tpl p-6" data-testid="forge-quick-launch">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg grid place-items-center"
                     style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(34,211,238,0.15))" }}>
                    <Zap size={18} style={{ color: "#8b5cf6" }} />
                </div>
                <div className="flex-1">
                    <h3 className="text-[16px] font-extrabold" style={{ color: "var(--navy)" }}>
                        FORGE · Cognitive OS
                    </h3>
                    <p className="text-[13px] mt-0.5" style={{ color: "var(--text-2)" }}>
                        Accesso rapido a Forge. L'URL è editabile — punta all'installazione locale
                        (<code className="mono">/forge</code>) o a una VPS esterna
                        (<code className="mono">https://forge.tuodominio.com</code>).
                    </p>
                </div>
            </div>

            {!editing ? (
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex-1 min-w-[200px] px-3 py-2 rounded-md mono text-[13px]"
                         style={{ background: "var(--mod-bg-2)", color: "var(--text-1)", border: "1px solid var(--border)" }}
                         data-testid="forge-quick-launch-url">
                        {url}
                    </div>
                    <button
                        onClick={openForge}
                        data-testid="forge-quick-launch-open"
                        className="px-4 py-2 rounded-md text-white text-[13px] font-semibold flex items-center gap-2 hover:opacity-90"
                        style={{ background: "linear-gradient(135deg, #8b5cf6, #22d3ee)" }}
                    >
                        <LogIn size={14} /> Apri Forge
                    </button>
                    <button
                        onClick={() => { setDraft(url); setEditing(true); }}
                        data-testid="forge-quick-launch-edit"
                        className="px-3 py-2 rounded-md text-[13px] font-semibold hover:bg-gray-50"
                        style={{ border: "1px solid var(--border)", color: "var(--text-1)" }}
                    >
                        ✎ Modifica URL
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                        placeholder="/forge  oppure  https://forge.tuodominio.com"
                        data-testid="forge-quick-launch-input"
                        autoFocus
                        className="flex-1 min-w-[200px] px-3 py-2 rounded-md mono text-[13px]"
                        style={{ background: "var(--mod-bg-2)", color: "var(--text-1)", border: "1px solid var(--mod-accent)" }}
                    />
                    <button
                        onClick={save}
                        data-testid="forge-quick-launch-save"
                        className="px-4 py-2 rounded-md text-white text-[13px] font-semibold hover:opacity-90"
                        style={{ background: "#10b981" }}
                    >
                        <Check size={14} className="inline" /> Salva
                    </button>
                    <button
                        onClick={() => setEditing(false)}
                        className="px-3 py-2 rounded-md text-[13px] font-semibold hover:bg-gray-50"
                        style={{ border: "1px solid var(--border)", color: "var(--text-1)" }}
                    >
                        Annulla
                    </button>
                    <button
                        onClick={reset}
                        data-testid="forge-quick-launch-reset"
                        className="px-3 py-2 rounded-md text-[13px] font-semibold hover:bg-red-50"
                        style={{ border: "1px solid var(--border)", color: "#dc2626" }}
                        title="Ripristina URL di default"
                    >
                        ↺ Reset
                    </button>
                </div>
            )}
        </div>
    );
}
